import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { Api, ApiQueryOption } from '@shared/constants/api';
import { SharedCommonUtility } from '@shared/utils/common.utility';

import { CustomValidators } from '../../../../services/helpers/form-custom-validators';
import { FormService } from '../../../../services/form.service';
import { TranslateService } from '../../../../translate/translate.service';
import { HealthcheckService } from '../../../../services/healthcheck.service';
import { ErrorMessageService } from '../../../../services/error-message.service';
import { redirectUrl } from '../../../../shared/constants';

enum FormField {
  'organizationUrl' = 'organizationUrl',
}

@Component({
  selector: 'app-tenant-login-redirect',
  templateUrl: './tenant-login-redirect.component.html',
  styleUrl: './tenant-login-redirect.component.scss',
  standalone: false,
})
export class TenantLoginRedirectComponent implements AfterViewInit {
  protected readonly FormField: typeof FormField = FormField;

  @ViewChild('orgUrlInput') public orgUrlInput: ElementRef<HTMLInputElement>;

  public formFieldErrors: Partial<Record<FormField, string>>;
  public formFieldsLabel: Record<FormField, string>;
  public form: UntypedFormGroup;

  private readonly returnTo: string | undefined;
  private readonly userwayAccountCode: string | undefined;
  private readonly userwayFailureRedirectUri: string | undefined;
  private readonly userwayState: string | undefined;
  private readonly callerOrigin: string | undefined;

  constructor(
    private formBuilder: FormBuilder,
    private formService: FormService,
    private translateService: TranslateService,
    private healthcheckService: HealthcheckService,
    private errorMessageService: ErrorMessageService,
    private element: ElementRef<Element>,
    private activatedRoute: ActivatedRoute,
  ) {
    this.formFieldErrors = {};
    this.formFieldsLabel = {
      [FormField.organizationUrl]: 'organization_url',
    };
    this.form = this.formBuilder.group({
      [FormField.organizationUrl]: this.formBuilder.control('', {
        validators: [CustomValidators.required, CustomValidators.domainValidator],
        asyncValidators: this.healthcheckService.organizationURLAsyncValidator,
      }),
    });
    this.returnTo = this.activatedRoute.snapshot.queryParams[redirectUrl.returnTo];
    this.userwayAccountCode = this.activatedRoute.snapshot.queryParams[ApiQueryOption.userwayAccountCode];
    this.userwayFailureRedirectUri = this.activatedRoute.snapshot.queryParams[ApiQueryOption.userwayFailureRedirectUri];
    this.userwayState = this.activatedRoute.snapshot.queryParams[ApiQueryOption.userwayState];
    this.callerOrigin = this.activatedRoute.snapshot.queryParams[ApiQueryOption.callerOrigin];
  }

  public ngAfterViewInit(): void {
    this.orgUrlInput?.nativeElement.focus();
  }

  public checkValidity(): boolean {
    if (this.form.valid) {
      this.formFieldErrors = {};
      return true;
    }

    if (this.form.get(FormField.organizationUrl).hasError('invalidOrgURL')) {
      this.formFieldErrors[FormField.organizationUrl] = this.translateService.instant('non-existent-website-error');
    } else {
      this.formFieldErrors[FormField.organizationUrl] = this.formService.getErrorMessageForField(
        this.form.get(FormField.organizationUrl),
        FormField.organizationUrl,
        '',
        '',
        {
          required: this.translateService.instant('enter_a_url'),
        },
      );
    }
    this.errorMessageService.setFocusOnFirstError(this.element.nativeElement);

    return false;
  }

  public onSubmit(): void {
    if (!this.checkValidity()) {
      return;
    }

    const orgUrl: string = this.form.get(FormField.organizationUrl).value?.trim();
    const [, orgDomain] = SharedCommonUtility.getProtocolHostPortAndPath(orgUrl);

    const queryParams = new URLSearchParams();
    if (SharedCommonUtility.notNullishOrEmpty(this.returnTo)) {
      queryParams.set(redirectUrl.returnTo, this.returnTo);
    }
    if (SharedCommonUtility.notNullishOrEmpty(this.userwayAccountCode)) {
      queryParams.set(ApiQueryOption.userwayAccountCode, this.userwayAccountCode);
    }
    if (SharedCommonUtility.notNullishOrEmpty(this.userwayFailureRedirectUri)) {
      queryParams.set(ApiQueryOption.userwayFailureRedirectUri, this.userwayFailureRedirectUri);
    }
    if (SharedCommonUtility.notNullishOrEmpty(this.userwayState)) {
      queryParams.set(ApiQueryOption.userwayState, this.userwayState);
    }
    if (SharedCommonUtility.notNullishOrEmpty(this.callerOrigin)) {
      queryParams.set(ApiQueryOption.callerOrigin, this.callerOrigin);
    }

    const queryString: string = queryParams.toString();
    const redirectionLoginUrl: string = `https://${orgDomain}/${Api.login}${queryString ? `?${queryString}` : ''}`;

    window.open(redirectionLoginUrl, '_self');
  }
}
